import { useState } from "react";
import { motion } from "framer-motion";
import { X, Upload, FileSpreadsheet, Check, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { MODAL_OVERLAY, SLIDE_UP_LG } from "../lib/animations";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const BulkImport = ({ onClose, onImported }) => {
  const [csvText, setCsvText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const VALID_RANKS = ["SENIOR ADMIN", "ADMIN", "SENIOR MOD", "MOD", "JRMOD"];

  const parseCSV = () => {
    const lines = csvText.trim().split("\n").filter(l => l.trim());
    if (lines.length < 2) return toast.error("Need at least a header row and one data row");

    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    const nameIdx = headers.findIndex(h => h.includes("name") && !h.includes("user"));
    const usernameIdx = headers.findIndex(h => h.includes("username") || h.includes("roblox"));
    const rankIdx = headers.findIndex(h => h.includes("rank"));
    const discordIdx = headers.findIndex(h => h.includes("discord"));

    if (nameIdx === -1) return toast.error("CSV must have a 'name' column");

    const staff = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map(c => c.trim());
      const name = cols[nameIdx] || "";
      if (!name) continue;

      staff.push({
        name,
        username: usernameIdx >= 0 ? (cols[usernameIdx] || name) : name,
        rank: rankIdx >= 0 ? (cols[rankIdx] || "JRMOD").toUpperCase() : "JRMOD",
        discord_id: discordIdx >= 0 ? (cols[discordIdx] || "0") : "0",
      });
    }

    if (staff.length === 0) return toast.error("No valid staff entries found");
    setParsed(staff);
    toast.success(`Parsed ${staff.length} staff members`);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCsvText(ev.target.result);
      toast.success(`Loaded ${file.name}`);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!parsed?.length) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/staff/bulk-import`, { staff: parsed });
      setResult(res.data);
      toast.success(`Imported ${res.data.imported} staff members`);
      if (onImported) onImported();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <motion.div
      {...MODAL_OVERLAY}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="glass-card w-full max-w-3xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-import-modal"
      >
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-none bg-cyan-500/20 border border-cyan-500/30">
                <FileSpreadsheet className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-white">Bulk Import Staff</h2>
                <p className="text-zinc-400 text-sm">Import from CSV — columns: name, username, rank, discord_id</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-none">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-100px)]">
          {result ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-heading font-bold text-white mb-2">Import Complete</h3>
              <p className="text-emerald-400 text-lg">{result.imported} staff imported</p>
              {result.skipped > 0 && <p className="text-amber-400 text-sm mt-1">{result.skipped} skipped (duplicates)</p>}
              <button onClick={onClose} className="mt-6 btn-primary" data-testid="import-done-btn">Done</button>
            </div>
          ) : (
            <>
              {/* File Upload */}
              <div>
                <label className="block mb-2 text-sm text-zinc-400">Upload CSV File</label>
                <label className="flex items-center gap-3 p-4 rounded-none border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 cursor-pointer transition-colors">
                  <Upload className="w-5 h-5 text-zinc-500" />
                  <span className="text-zinc-400 text-sm">Click to upload or drag a CSV file</span>
                  <input type="file" accept=".csv,.txt" onChange={handleFileUpload} className="hidden" data-testid="csv-file-input" />
                </label>
              </div>

              {/* Or paste CSV */}
              <div>
                <label className="block mb-2 text-sm text-zinc-400">Or paste CSV data</label>
                <textarea
                  value={csvText}
                  onChange={(e) => { setCsvText(e.target.value); setParsed(null); }}
                  placeholder={`name,username,rank,discord_id\nJohn,JohnDoe,MOD,123456789\nJane,JaneDoe,ADMIN,987654321`}
                  className="input-field w-full h-32 font-mono text-sm"
                  data-testid="csv-text-input"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={parseCSV}
                  disabled={!csvText.trim()}
                  className="btn-primary disabled:opacity-50"
                  data-testid="parse-csv-btn"
                >
                  Parse CSV
                </button>
              </div>

              {/* Preview */}
              {parsed && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold text-white">Preview ({parsed.length} staff)</h3>
                    <button
                      onClick={handleImport}
                      disabled={importing}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2 px-6 rounded transition-colors disabled:opacity-50"
                      data-testid="confirm-import-btn"
                    >
                      {importing ? "Importing..." : `Import ${parsed.length} Staff`}
                    </button>
                  </div>

                  <div className="max-h-[300px] overflow-y-auto scrollbar-thin">
                    <table className="w-full">
                      <thead>
                        <tr className="table-header">
                          <th className="text-left py-2 px-3">Name</th>
                          <th className="text-left py-2 px-3">Username</th>
                          <th className="text-left py-2 px-3">Rank</th>
                          <th className="text-left py-2 px-3">Discord ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.map((s) => (
                          <tr key={`${s.username}-${s.discord_id}`} className="table-row">
                            <td className="py-2 px-3 text-white text-sm">{s.name}</td>
                            <td className="py-2 px-3 text-zinc-400 text-sm">{s.username}</td>
                            <td className="py-2 px-3">
                              <span className={`text-xs font-bold ${VALID_RANKS.includes(s.rank) ? "text-cyan-400" : "text-amber-400"}`}>
                                {s.rank}
                                {!VALID_RANKS.includes(s.rank) && " (?)"}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-zinc-500 font-mono text-xs">{s.discord_id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {parsed.some(s => !VALID_RANKS.includes(s.rank)) && (
                    <div className="flex items-center gap-2 text-amber-400 text-xs">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Some ranks are non-standard. Valid ranks: {VALID_RANKS.join(", ")}</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BulkImport;
